import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, ExternalLink, Gift } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/Shared";
import { api, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BASE = ["Devis & calcul de volume", "CRM clients", "Planning des déménagements", "Équipes & véhicules", "Facturation & e-mails"];
const EXTRA = {
  starter: [],
  pro: ["Application Nomade (tablette, hors-ligne)", "Factur-X & Plateforme Agréée", "Export comptable (FEC, Sage, EBP, Quadra…)", "Itinéraire du jour"],
  premium: ["Tout Pro inclus", "Estimateur de volume IA", "Assistant commercial IA", "Itinéraire optimisé"],
};
const DESC = { starter: "Petites entreprises", pro: "Entreprises structurées", premium: "Multi-agences et IA" };

function PlanCard({ plan, current, busy, onChoose }) {
  const isCurrent = current === plan.id;
  const items = plan.id === "premium" ? EXTRA.premium : [...BASE, ...EXTRA[plan.id]];
  return (
    <div data-testid={`plan-card-${plan.id}`} className={`flex flex-col rounded-2xl border bg-white p-6 ${plan.id === "pro" ? "border-[#1559d6] shadow-lg" : "border-slate-200"}`}>
      <p className="text-xs font-semibold uppercase tracking-widest text-teal-600">{DESC[plan.id]}</p>
      <h3 className="mt-1 font-display text-2xl font-bold text-slate-900">{plan.name}</h3>
      <p className="mt-4 font-display text-3xl font-bold text-[#0b2c6b]" data-testid={`plan-price-${plan.id}`}>{plan.price} €<span className="text-sm font-medium text-slate-400"> HT/mois</span></p>
      <p className="mt-1 text-xs text-slate-500">{plan.max_users ? `Jusqu'à ${plan.max_users} utilisateurs` : "Utilisateurs illimités"}</p>
      <ul className="mt-5 flex-1 space-y-2 text-sm text-slate-700">
        {items.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />{f}</li>)}
      </ul>
      <Button data-testid={`plan-choose-${plan.id}`} disabled={isCurrent || busy} onClick={() => onChoose(plan.id)}
        className={`mt-6 ${isCurrent ? "bg-slate-200 text-slate-600" : "bg-[#0b2c6b] hover:bg-[#1559d6]"}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Formule actuelle" : "Choisir cette formule"}
      </Button>
    </div>
  );
}

export default function Subscription() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();
  const load = () => api.get("/billing").then((r) => setData(r.data));
  useEffect(() => {
    load();
    if (params.get("annule")) toast.info("Paiement annulé, aucune somme n'a été prélevée");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (plan) => {
    const active = data.subscription.plan !== "trial" && data.subscription.plan !== "none";
    if (active && !window.confirm("Changer de formule ? La différence sera calculée au prorata sur votre prochaine facture.")) return;
    setBusy(plan);
    try {
      const r = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Formule modifiée");
      const me = await api.get("/auth/me");
      setUser(me.data);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy("");
  };
  const portal = async () => {
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) return null;
  const sub = data.subscription;
  return (
    <div className="fade-up">
      <PageHeader overline="Compte" title="Abonnement" subtitle="Choisissez la formule adaptée à votre entreprise. Paiement mensuel sécurisé par carte, sans engagement." />
      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="subscription-current">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Formule actuelle</p>
          <p className="text-lg font-bold text-slate-900" data-testid="subscription-plan-name">{sub.plan_name}</p>
          <p className="text-sm text-slate-500" data-testid="subscription-status">
            {sub.plan === "trial" ? `Essai gratuit jusqu'au ${fmtDate(sub.trial_ends_at)} — toutes les fonctions Premium` : sub.plan === "none" ? "Essai terminé — choisissez une formule pour continuer" : sub.status === "past_due" ? "Paiement en échec — mettez à jour votre carte" : "Abonnement actif"}
            {` · ${data.users_count} utilisateur(s)`}
          </p>
        </div>
        {data.has_stripe_customer && <Button variant="outline" data-testid="subscription-portal-button" onClick={portal}><ExternalLink className="mr-2 h-4 w-4" />Gérer mon paiement / résilier</Button>}
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {data.plans.map((p) => <PlanCard key={p.id} plan={p} current={sub.plan} busy={busy === p.id} onChoose={choose} />)}
      </div>
      <p className="mt-5 flex items-center gap-2 text-sm text-teal-700"><Gift className="h-4 w-4" />Tablette Nomade 7" offerte avec chaque nouvel abonnement.</p>
      {user?.role !== "owner" && <p className="mt-2 text-xs text-slate-500">Seul le dirigeant peut modifier l'abonnement.</p>}
    </div>
  );
}
